import { request as http, getTokenManager } from '@/utils/http'
import type {
  ApiResponse,
  PageResponse,
  BatchDeleteRequest,
  BatchUpdateValidDateRequest,
  BatchUpdateTimeRequest,
  BatchUpdatePriceRequest,
  BatchModifyFclRequest,
  BatchModifyFclResult,
  PriceFclPageRequest,
  PriceFclListVO,
  PriceFclVO,
  PriceFclSaveRequest,
  PriceFclUpdateRequest,
  PriceFclBatchDetailVO,
  PriceBatchOnlineByBatchRequest,
  BatchPublishResult,
  PriceFclExcelImportResultVO,
  PriceFclMatchResultVO
} from './types'

// 分页查询整箱运价
export const page = (params: PriceFclPageRequest) => {
  return http.post<PageResponse<PriceFclListVO>>('/price/fcl/page', params)
}

// 新增整箱运价
export const save = (params: PriceFclSaveRequest) => {
  return http.post<boolean>('/price/fcl/save', params)
}

// 修改整箱运价
export const update = (params: PriceFclUpdateRequest) => {
  return http.post<boolean>('/price/fcl/update', params)
}

// 获取整箱运价详情
export const get = (id: string) => {
  return http.post<ApiResponse<PriceFclVO>>('/price/fcl/get', { id })
}

// 批量上架整箱运价
export const batchOnline = (params: BatchDeleteRequest) => {
  return http.post<number>('/price/fcl/batch-online', params)
}

export const precheckBatchOnline = (params: BatchDeleteRequest) => {
  return http.post<boolean>('/price/fcl/batch-online-precheck', params)
}

export const precheckBatchOffline = (params: BatchDeleteRequest) => {
  return http.post<boolean>('/price/fcl/batch-offline-precheck', params)
}

export const precheckBatchDelete = (params: BatchDeleteRequest) => {
  return http.post<boolean>('/price/fcl/batch-delete-precheck', params)
}

// 根据批次号批量上架整箱
export const batchOnlineByBatch = (data: PriceBatchOnlineByBatchRequest) => {
  return http.post<BatchPublishResult>('/price/fcl/batch-online-by-batch', data)
}

// 批量下架整箱运价
export const batchOffline = (params: BatchDeleteRequest) => {
  return http.post<number>('/price/fcl/batch-offline', params)
}

// 批量修改有效期
export const batchUpdateValidDate = (params: BatchUpdateValidDateRequest) => {
  return http.post<number>('/price/fcl/batch-update-valid-date', params)
}

// 批量修改时间（统一接口）
export const batchUpdateTime = (params: BatchUpdateTimeRequest) => {
  return http.post<number>('/price/fcl/batch-update-time', params)
}

// 批量修改价格
export const batchUpdatePrice = (params: BatchUpdatePriceRequest) => {
  return http.post<number>('/price/fcl/batch-update-price', params)
}

// 统一批量修改 FCL 运价
export const batchModify = (params: BatchModifyFclRequest) => {
  return http.post<BatchModifyFclResult>('/price/fcl/batch-modify', params)
}

// 批量删除整箱运价
export const batchDelete = (params: BatchDeleteRequest) => {
  return http.post<number>('/price/fcl/batch-delete', params)
}

// 根据IDs查询运价明细（批量改价用）
export const getBatchPriceDetails = (params: BatchDeleteRequest) => {
  return http.post<ApiResponse<PriceFclBatchDetailVO[]>>('/price/fcl/batch-price-details', params)
}

/** 整箱运价 Excel 导入（FCL3.1 模板，multipart 字段名 file） */
export const importExcel = (formData: FormData) => {
  return http.post<PriceFclExcelImportResultVO>('/price/fcl/excel/import', formData)
}

function resolveFileNameFromContentDisposition(contentDisposition: string | null): string | undefined {
  if (!contentDisposition) {
    return undefined
  }
  // RFC 5987: filename*=UTF-8''...
  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]).trim()
    } catch {
      // ignore
    }
  }
  const normalMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i)
  if (normalMatch?.[1]) {
    return normalMatch[1].trim()
  }
  return undefined
}

/** 下载 FCL3.1 导入模板（按文件ID），优先保留服务端原始文件名 */
export const downloadExcelTemplate = () => {
  const fileId =
    import.meta.env.VITE_FCL_IMPORT_TEMPLATE_FILE_ID || '69d4b505109582295df7f287'
  const token = getTokenManager().get()
  return fetch(`/gateway/universal/storage/findByFileId/${fileId}`, {
    method: 'GET',
    headers: token ? { Authorization: token } : undefined
  }).then(async res => {
    if (!res.ok) {
      throw new Error(`Download failed: ${res.status}`)
    }
    const blob = await res.blob()
    const fileName = resolveFileNameFromContentDisposition(res.headers.get('content-disposition'))
    return {
      blob,
      fileName
    }
  })
}

/** 获取文字运价解析预览数据 (CCTOWER-3605) */
export const getTextImportPreview = (batchNo: number | string) => {
  return http.get<ApiResponse<any>>('/price/fcl/text-import/preview', { batchNo })
}

/** 确认文字运价导入生效 (CCTOWER-3605) */
export const confirmTextImport = (data: { batchNo: number | string; finalJson?: string }) => {
  return http.post<ApiResponse<any>>('/price/fcl/confirm-import-from-quotation', {
    batchNo: data.batchNo,
    source: 6, // FclTextRateImportFacade.SOURCE_AI_TEXT_PARSE
    finalJson: data.finalJson
  })
}

/** 放弃/取消文字运价导入草稿 (CCTOWER-3605) */
export const cancelTextImport = (data: { batchNo: number | string }) => {
  return http.post<ApiResponse<any>>('/price/fcl/cancel-import-from-quotation', {
    batchNo: data.batchNo,
    source: 6 // FclTextRateImportFacade.SOURCE_AI_TEXT_PARSE
  })
}
